'use client';

import { amplifyConfig, reCaptchaKey } from '@/lib/constants';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { Amplify, Auth } from 'aws-amplify';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'sonner';

type AuthView = 'login-signup' | 'forgot-password' | 'confirmation-code';

const validateName = (name: string) => {
    return name.length >= 2;
};

const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const validatePassword = (password: string) => {
    const requirements = {
        minLength: password.length >= 8,
        requireLowercase: /[a-z]/.test(password),
        requireUppercase: /[A-Z]/.test(password),
        requireDigits: /\d/.test(password),
        requireSymbols: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    const isValid = Object.values(requirements).every(Boolean);
    return { isValid, requirements };
};

type AuthFormContentProps = {
    initialMode?: 'login' | 'signup';
};

function AuthFormContent({ initialMode = 'login' }: AuthFormContentProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [isLogin, setIsLogin] = useState(initialMode === 'login');
    const [view, setView] = useState<AuthView>('login-signup');
    const [agreementsAccepted, setAgreementsAccepted] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        requireLowercase: false,
        requireUppercase: false,
        requireDigits: false,
        requireSymbols: false,
    });
    const [showPasswordHint, setShowPasswordHint] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha() ?? {};
    const router = useRouter();
    const searchParams = useSearchParams();

    // Allow callers (e.g. /oauth/consent) to bounce the user through login
    // and come back to a specific URL. The redirect target is constrained to
    // same-origin paths to prevent open-redirect abuse.
    const safeRedirectTarget = (() => {
        const raw = searchParams?.get('redirect');
        if (!raw) return null;
        if (!raw.startsWith('/') || raw.startsWith('//')) return null;
        return raw;
    })();

    // Check if form is valid for submission
    const isFormValid = () => {
        if (isLogin) {
            return validateEmail(email) && password.length > 0;
        } else {
            const { isValid: passwordValid } = validatePassword(password);
            return (
                validateName(firstName) &&
                validateName(lastName) &&
                validateEmail(email) &&
                passwordValid &&
                agreementsAccepted
            );
        }
    };

    // Handle password input with validation
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);

        if (!isLogin) {
            const { requirements } = validatePassword(newPassword);
            setPasswordRequirements(requirements);
        }
    };

    useEffect(() => {
        // Configure Amplify
        if (typeof window !== 'undefined') {
            Amplify.configure(amplifyConfig);
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        if (!isLogin && !executeRecaptcha) {
            toast.error('reCAPTCHA not yet available');
            return;
        }

        try {
            if (isLogin) {
                const user = await Auth.signIn(email, password);
                console.log('User signed in successfully:', user);

                // Wait a bit for session to be fully established before redirecting
                await new Promise(resolve => setTimeout(resolve, 500));

                // Use window.location.href for full page reload to ensure session is picked up.
                // `/dashboard` is the landing route: it sends Aleph staff to Overview and
                // everyone else to Employees.
                window.location.href = safeRedirectTarget ?? '/dashboard';
            } else {
                if (!validateName(firstName)) {
                    toast.error('First name must be at least 2 characters long');
                    return;
                }

                if (!validateName(lastName)) {
                    toast.error('Last name must be at least 2 characters long');
                    return;
                }

                if (!agreementsAccepted) {
                    toast.error('Please accept the Terms of Service and Privacy Policy to continue');
                    return;
                }

                const token = await executeRecaptcha?.('signup');
                if (!token) {
                    throw new Error('Failed to execute reCAPTCHA');
                }

                await Auth.signUp({
                    username: email,
                    password,
                    attributes: {
                        email,
                        given_name: firstName,
                        family_name: lastName,
                    },
                    validationData: {
                        recaptchaToken: token,
                    },
                });

                router.push(`/verify?email=${encodeURIComponent(email)}`);
            }
        } catch (error) {
            toast.error((error as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await Auth.forgotPassword(email);
            toast.success('Verification code sent to your email');
            setView('confirmation-code');
        } catch (error) {
            toast.error((error as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await Auth.forgotPasswordSubmit(email, resetCode, newPassword);
            toast.success('Password reset successfully!');
            setPassword(newPassword);
            setView('login-signup');
            setIsLogin(true);
        } catch (error) {
            toast.error((error as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleSocialSignIn = async (provider: CognitoHostedUIIdentityProvider) => {
        try {
            setOauthLoading(true);
            await Auth.federatedSignIn({ provider });
        } catch (error) {
            toast.error((error as Error).message);
            setOauthLoading(false);
        }
    };

    // Render the main login/signup form
    const renderLoginSignupForm = () => (
        <>
            <h1 className="text-[32px] font-semibold text-center text-[#111827] tracking-[-0.02em]">
                {isLogin ? '👋 Welcome back' : 'Create an account'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                    {!isLogin && (
                        <>
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                                    First name
                                </label>
                                <input
                                    id="firstName"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                                    placeholder="John"
                                />
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                                    Last name
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                                    placeholder="Doe"
                                />
                            </div>
                        </>
                    )}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email address
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                            placeholder="name@company.com"
                        />
                    </div>
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            {isLogin && (
                                <button
                                    type="button"
                                    onClick={() => setView('forgot-password')}
                                    className="text-sm text-[#111827] hover:text-[#1f2937] font-medium"
                                >
                                    Forgot password?
                                </button>
                            )}
                        </div>
                        <input
                            id="password"
                            type="password"
                            required
                            value={password}
                            onChange={handlePasswordChange}
                            onFocus={() => !isLogin && setShowPasswordHint(true)}
                            onBlur={() => setShowPasswordHint(false)}
                            className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                            placeholder="••••••••"
                        />
                        {!isLogin && showPasswordHint && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                                <p className="text-sm font-medium text-gray-700 mb-2">Password must contain:</p>
                                <ul className="space-y-1 text-xs">
                                    <li
                                        className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}
                                    >
                                        <span className="mr-2">{passwordRequirements.minLength ? '✓' : '○'}</span>
                                        At least 8 characters
                                    </li>
                                    <li
                                        className={`flex items-center ${passwordRequirements.requireLowercase ? 'text-green-600' : 'text-gray-500'}`}
                                    >
                                        <span className="mr-2">
                                            {passwordRequirements.requireLowercase ? '✓' : '○'}
                                        </span>
                                        At least one lowercase letter
                                    </li>
                                    <li
                                        className={`flex items-center ${passwordRequirements.requireUppercase ? 'text-green-600' : 'text-gray-500'}`}
                                    >
                                        <span className="mr-2">
                                            {passwordRequirements.requireUppercase ? '✓' : '○'}
                                        </span>
                                        At least one uppercase letter
                                    </li>
                                    <li
                                        className={`flex items-center ${passwordRequirements.requireDigits ? 'text-green-600' : 'text-gray-500'}`}
                                    >
                                        <span className="mr-2">{passwordRequirements.requireDigits ? '✓' : '○'}</span>
                                        At least one number
                                    </li>
                                    <li
                                        className={`flex items-center ${passwordRequirements.requireSymbols ? 'text-green-600' : 'text-gray-500'}`}
                                    >
                                        <span className="mr-2">{passwordRequirements.requireSymbols ? '✓' : '○'}</span>
                                        At least one special character
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {!isLogin && (
                    <div className="flex items-start space-x-2">
                        <input
                            id="agreements"
                            type="checkbox"
                            checked={agreementsAccepted}
                            onChange={e => setAgreementsAccepted(e.target.checked)}
                            className="mt-1 h-4 w-4 text-[#111827] focus:ring-[#111827] border-gray-300 rounded"
                        />
                        <label htmlFor="agreements" className="text-sm text-gray-600">
                            I agree to the{' '}
                            <a
                                href="https://www.aleph.vc/legal/terms-of-service"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#111827] hover:text-[#1f2937] font-medium underline"
                            >
                                Terms of Service
                            </a>
                            {' and '}
                            <a
                                href="https://www.aleph.vc/legal/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#111827] hover:text-[#1f2937] font-medium underline"
                            >
                                Privacy Policy
                            </a>
                        </label>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading || !isFormValid()}
                    className="w-full px-4 py-[10px] text-[14px] font-medium text-white bg-[#6346cd] rounded-3xl hover:bg-[#5538b5] focus:outline-none focus:ring-2 focus:ring-[#6346cd] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Continue'}
                </button>
            </form>

            <div className="text-center">
                <p className="text-[15px] text-gray-600">
                    {isLogin ? "Don't have an account? " : 'Already have an account? '}
                    <a
                        href={isLogin ? '/signup' : '/auth'}
                        className="text-[#111827] hover:text-[#1f2937] font-bold underline"
                    >
                        {isLogin ? 'Sign up' : 'Sign in'}
                    </a>
                </p>
            </div>

            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-[#FAFAFA] text-gray-500">or continue with</span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => handleSocialSignIn(CognitoHostedUIIdentityProvider.Google)}
                    disabled={oauthLoading}
                    className="flex items-center justify-center px-3 py-[10px] text-[15px] text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#111827] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {oauthLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <>
                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                <path
                                    fill="#4285F4"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="#34A853"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="#FBBC05"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="#EA4335"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            Google
                        </>
                    )}
                </button>

                <button
                    type="button"
                    onClick={() => handleSocialSignIn(CognitoHostedUIIdentityProvider.Cognito)}
                    disabled={oauthLoading}
                    className="flex items-center justify-center px-3 py-[10px] text-[15px] text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#111827] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {oauthLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <>
                            <svg className="w-5 h-5 text-[#0A66C2] mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                            </svg>
                            LinkedIn
                        </>
                    )}
                </button>
            </div>
        </>
    );

    // Render the forgot password form
    const renderForgotPasswordForm = () => (
        <>
            <div className="mb-6">
                <button
                    type="button"
                    onClick={() => setView('login-signup')}
                    className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to login
                </button>
            </div>

            <h1 className="text-[32px] font-semibold text-center text-[#111827] tracking-[-0.02em] mb-4">
                Reset your password
            </h1>

            <p className="text-center text-gray-600 mb-8">
                Enter your email address and we&apos;ll send you a code to reset your password.
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email address
                    </label>
                    <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                        placeholder="name@company.com"
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-[10px] text-[14px] font-medium text-white bg-[#6346cd] rounded-3xl hover:bg-[#5538b5] focus:outline-none focus:ring-2 focus:ring-[#6346cd] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send reset code'}
                </button>
            </form>
        </>
    );

    // Render the code confirmation form
    const renderConfirmationForm = () => (
        <>
            <div className="mb-6">
                <button
                    type="button"
                    onClick={() => setView('forgot-password')}
                    className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                </button>
            </div>

            <h1 className="text-[32px] font-semibold text-center text-[#111827] tracking-[-0.02em] mb-4">
                Enter verification code
            </h1>

            <p className="text-center text-gray-600 mb-8">
                We&apos;ve sent a code to <strong>{email}</strong>. Enter it below along with your new password.
            </p>

            <form onSubmit={handleConfirmNewPassword} className="space-y-6">
                <div>
                    <label htmlFor="resetCode" className="block text-sm font-medium text-gray-700 mb-1">
                        Verification code
                    </label>
                    <input
                        id="resetCode"
                        type="text"
                        required
                        value={resetCode}
                        onChange={e => setResetCode(e.target.value)}
                        className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                        placeholder="Enter code"
                    />
                </div>

                <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        New password
                    </label>
                    <input
                        id="newPassword"
                        type="password"
                        required
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full px-3 py-[10px] text-[15px] text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#111827] focus:border-[#111827] transition-colors"
                        placeholder="••••••••"
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-[10px] text-[14px] font-medium text-white bg-[#6346cd] rounded-3xl hover:bg-[#5538b5] focus:outline-none focus:ring-2 focus:ring-[#6346cd] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Reset password'}
                </button>
            </form>
        </>
    );

    // Render the appropriate view
    const renderView = () => {
        switch (view) {
            case 'forgot-password':
                return renderForgotPasswordForm();
            case 'confirmation-code':
                return renderConfirmationForm();
            default:
                return renderLoginSignupForm();
        }
    };

    return <div className="w-full max-w-[400px] space-y-10">{renderView()}</div>;
}

type AuthFormProps = {
    initialMode?: 'login' | 'signup';
};

export default function AuthForm({ initialMode = 'login' }: AuthFormProps) {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={reCaptchaKey}
            scriptProps={{
                async: true,
                defer: true,
                appendTo: 'head',
            }}
        >
            <React.Suspense
                fallback={
                    <div className="flex items-center justify-center p-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                }
            >
                <AuthFormContent initialMode={initialMode} />
            </React.Suspense>
        </GoogleReCaptchaProvider>
    );
}
